import { useEffect, useState, useRef } from 'react'
import api from '../lib/api'
import {
  FileText, Upload, Trash2, Download, FolderOpen, ChevronDown,
  ChevronRight, Search, Eye, EyeOff, Clock, Shield, Send,
  Archive, History, AlertTriangle, FileSpreadsheet, X, Check
} from 'lucide-react'

interface Employee { id: string; firstName: string; lastName: string }
interface Document {
  id: string; name: string; documentType: string
  fileName: string; fileSize: number; mimeType: string | null
  createdAt: string; period: string | null
  isPublished: boolean; publishedAt: string | null
  isConfidential: boolean; archivedAt: string | null
  retentionYears: number; uploadedBy: string | null
  employee: { id: string; firstName: string; lastName: string }
}
interface AccessLog {
  id: string; accessedBy: string; accessorType: string
  accessType: string; accessedAt: string; ipAddress: string | null
}

const DOC_TYPES = [
  { value: 'contrat',        label: 'Contrat de travail',   color: 'bg-blue-100 text-blue-700',    emoji: '📋' },
  { value: 'avenant',        label: 'Avenant',              color: 'bg-indigo-100 text-indigo-700', emoji: '📝' },
  { value: 'bulletin_paie',  label: 'Bulletin de paie',     color: 'bg-green-100 text-green-700',   emoji: '💶' },
  { value: 'fiche_paie',     label: 'Fiche de paie',        color: 'bg-teal-100 text-teal-700',     emoji: '💶' },
  { value: 'rib',            label: 'RIB / IBAN',           color: 'bg-yellow-100 text-yellow-700', emoji: '🏦' },
  { value: 'identite',       label: "Pièce d'identité",     color: 'bg-orange-100 text-orange-700', emoji: '🪪' },
  { value: 'mutuelle',       label: 'Mutuelle',             color: 'bg-pink-100 text-pink-700',     emoji: '🏥' },
  { value: 'arret_maladie',  label: 'Arrêt maladie',        color: 'bg-red-100 text-red-700',       emoji: '🤒' },
  { value: 'diplome',        label: 'Diplôme / Formation',  color: 'bg-purple-100 text-purple-700', emoji: '🎓' },
  { value: 'autre',          label: 'Autre',                color: 'bg-gray-100 text-gray-600',     emoji: '📎' },
]

const ACCESS_LABELS: Record<string, string> = {
  upload: 'Ajout', download: 'Téléchargement', publish: 'Publication',
  unpublish: 'Dépublié', delete: 'Suppression', archive: 'Archivage'
}

function typeInfo(type: string) { return DOC_TYPES.find(t => t.value === type) || DOC_TYPES[DOC_TYPES.length - 1] }
function fmtSize(bytes: number) {
  if (bytes < 1024) return `${bytes} o`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`
}
function fmtDate(iso: string) { return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) }
function retentionExpiry(doc: Document): string | null {
  if (!doc.createdAt) return null
  const d = new Date(doc.createdAt)
  d.setFullYear(d.getFullYear() + (doc.retentionYears || 5))
  return d.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })
}

type TabKey = 'all' | 'bulletins' | 'archived'

export default function Documents() {
  const [employees, setEmployees] = useState<Employee[]>([])
  const [docs, setDocs] = useState<Document[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [filterType, setFilterType] = useState('')
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [activeTab, setActiveTab] = useState<TabKey>('all')
  const [modal, setModal] = useState(false)
  const [bulkModal, setBulkModal] = useState(false)
  const [logModal, setLogModal] = useState<{ doc: Document; logs: AccessLog[] } | null>(null)
  const [form, setForm] = useState({ employeeId: '', name: '', documentType: 'contrat', isConfidential: false, retentionYears: 5, period: '' })
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  // Bulk bulletin state
  const [bulkFiles, setBulkFiles] = useState<File[]>([])
  const [bulkMapping, setBulkMapping] = useState<Record<number, string>>({})
  const [bulkPeriod, setBulkPeriod] = useState('')
  const [bulkAutoPublish, setBulkAutoPublish] = useState(true)
  const [bulkUploading, setBulkUploading] = useState(false)
  const bulkFileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    api.get('/employees').then(r => {
      setEmployees(r.data)
      const exp: Record<string, boolean> = {}
      r.data.forEach((e: Employee) => { exp[e.id] = true })
      setExpanded(exp)
    })
    loadDocs()
  }, [])

  async function loadDocs() {
    setLoading(true)
    try {
      const r = await api.get('/documents')
      setDocs(r.data)
    } finally { setLoading(false) }
  }

  async function upload() {
    if (!file || !form.employeeId || !form.name) return
    setUploading(true); setUploadError('')
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('employeeId', form.employeeId)
      fd.append('name', form.name)
      fd.append('documentType', form.documentType)
      fd.append('isConfidential', String(form.isConfidential))
      fd.append('retentionYears', String(form.retentionYears))
      if (form.period) fd.append('period', form.period)
      await api.post('/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setModal(false)
      setForm({ employeeId: '', name: '', documentType: 'contrat', isConfidential: false, retentionYears: 5, period: '' })
      setFile(null)
      await loadDocs()
    } catch (e: any) {
      setUploadError(e.response?.data?.error || 'Erreur upload')
    } finally { setUploading(false) }
  }

  async function bulkUpload() {
    if (!bulkFiles.length) return
    setBulkUploading(true)
    try {
      const fd = new FormData()
      bulkFiles.forEach(f => fd.append('files', f))
      fd.append('employeeIds', JSON.stringify(bulkFiles.map((_, i) => bulkMapping[i] || '')))
      fd.append('documentType', 'bulletin_paie')
      fd.append('period', bulkPeriod)
      fd.append('autoPublish', String(bulkAutoPublish))
      const r = await api.post('/documents/bulk', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setBulkModal(false); setBulkFiles([]); setBulkMapping({}); setBulkPeriod('')
      alert(`${r.data.created} bulletin(s) importé(s)${bulkAutoPublish ? ' et publié(s)' : ''}.`)
      await loadDocs()
    } catch (e: any) {
      alert(e.response?.data?.error || 'Erreur import')
    } finally { setBulkUploading(false) }
  }

  async function togglePublish(doc: Document) {
    if (doc.isPublished) {
      await api.post(`/documents/${doc.id}/unpublish`)
    } else {
      await api.post(`/documents/${doc.id}/publish`)
    }
    await loadDocs()
  }

  async function archiveDoc(doc: Document) {
    if (!confirm('Archiver ce document ? Il ne sera plus visible par l\'employé.')) return
    await api.post(`/documents/${doc.id}/archive`)
    await loadDocs()
  }

  async function deleteDoc(id: string) {
    if (!confirm('Supprimer définitivement ce document ?')) return
    await api.delete(`/documents/${id}`)
    setDocs(prev => prev.filter(d => d.id !== id))
  }

  async function showLog(doc: Document) {
    const r = await api.get(`/documents/${doc.id}/access-log`)
    setLogModal({ doc, logs: r.data })
  }

  function downloadDoc(doc: Document) {
    window.open(`/api/documents/${doc.id}/download`, '_blank')
  }

  // Filter docs per tab
  const tabFiltered = docs.filter(d => {
    if (activeTab === 'archived') return !!d.archivedAt
    if (activeTab === 'bulletins') return d.documentType === 'bulletin_paie' && !d.archivedAt
    return !d.archivedAt
  })

  const filtered = tabFiltered.filter(d => {
    const q = search.toLowerCase()
    const matchSearch = !search ||
      d.name.toLowerCase().includes(q) ||
      d.employee.firstName.toLowerCase().includes(q) ||
      d.employee.lastName.toLowerCase().includes(q) ||
      (d.period || '').includes(q)
    const matchType = !filterType || d.documentType === filterType
    return matchSearch && matchType
  })

  const byEmployee = employees.map(emp => ({
    emp,
    docs: filtered.filter(d => d.employee.id === emp.id)
  })).filter(g => g.docs.length > 0)

  const publishedCount = docs.filter(d => d.isPublished && !d.archivedAt).length
  const bulletinCount = docs.filter(d => d.documentType === 'bulletin_paie').length

  const inputCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300'

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <FolderOpen size={22} className="text-blue-600" />
          <h1 className="text-xl font-bold text-gray-900">Documents RH</h1>
          <span className="text-sm text-gray-400">{docs.length} document{docs.length !== 1 ? 's' : ''}</span>
          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">{publishedCount} publiés</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setBulkModal(true)}
            className="flex items-center gap-2 border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium px-3 py-2 rounded-lg">
            <FileSpreadsheet size={15} /> Import bulletins
          </button>
          <button onClick={() => { setModal(true); setUploadError('') }}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg">
            <Upload size={15} /> Ajouter
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-xl p-1 w-fit">
        {([
          { key: 'all',      label: 'Tous' },
          { key: 'bulletins',label: `Bulletins (${bulletinCount})` },
          { key: 'archived', label: 'Archivés' },
        ] as const).map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === t.key ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher…"
            className="pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg w-56 focus:outline-none focus:ring-2 focus:ring-blue-300" />
        </div>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300">
          <option value="">Tous types</option>
          {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.emoji} {t.label}</option>)}
        </select>
        {(search || filterType) && (
          <button onClick={() => { setSearch(''); setFilterType('') }} className="text-xs text-gray-400 hover:text-gray-600">Effacer filtres</button>
        )}
      </div>

      {/* Document list */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 py-12 text-center text-gray-400 text-sm">Chargement...</div>
      ) : byEmployee.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 py-16 text-center">
          <FileText size={36} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-400 text-sm">Aucun document{activeTab === 'archived' ? ' archivé' : ''}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {byEmployee.map(({ emp, docs: empDocs }) => (
            <div key={emp.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <button onClick={() => setExpanded(e => ({ ...e, [emp.id]: !e[emp.id] }))}
                className="w-full flex items-center justify-between px-5 py-3 bg-gray-50 hover:bg-gray-100 transition-colors">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xs font-bold">
                    {emp.firstName[0]}{emp.lastName[0]}
                  </div>
                  <span className="font-semibold text-gray-900 text-sm">{emp.firstName} {emp.lastName}</span>
                  <span className="text-xs text-gray-400 bg-gray-200 rounded-full px-2 py-0.5">{empDocs.length}</span>
                  <span className="text-xs text-green-600 bg-green-50 rounded-full px-2 py-0.5">
                    {empDocs.filter(d => d.isPublished).length} publiés
                  </span>
                </div>
                {expanded[emp.id] ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
              </button>

              {expanded[emp.id] && (
                <div className="divide-y divide-gray-50">
                  {empDocs.map(doc => {
                    const ti = typeInfo(doc.documentType)
                    return (
                      <div key={doc.id} className={`flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors ${doc.archivedAt ? 'opacity-60' : ''}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-xl flex-shrink-0">{ti.emoji}</span>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-medium text-gray-900 truncate">{doc.name}</p>
                              {doc.isConfidential && <Shield size={12} className="text-orange-500 flex-shrink-0" title="Confidentiel — non visible par l'employé" />}
                              {doc.archivedAt && <Archive size={12} className="text-gray-400 flex-shrink-0" title="Archivé" />}
                            </div>
                            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                              <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${ti.color}`}>{ti.label}</span>
                              {doc.period && <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{doc.period}</span>}
                              <span className="text-xs text-gray-400">{fmtSize(doc.fileSize)}</span>
                              <span className="text-xs text-gray-300">•</span>
                              <span className="text-xs text-gray-400">{fmtDate(doc.createdAt)}</span>
                              {doc.isPublished ? (
                                <span className="flex items-center gap-0.5 text-xs text-green-600">
                                  <Eye size={10} /> Publié{doc.publishedAt ? ` ${fmtDate(doc.publishedAt)}` : ''}
                                </span>
                              ) : (
                                <span className="flex items-center gap-0.5 text-xs text-gray-400">
                                  <EyeOff size={10} /> Non publié
                                </span>
                              )}
                              <span className="flex items-center gap-0.5 text-xs text-gray-300" title={`Conservation : ${doc.retentionYears} ans — jusqu'à ${retentionExpiry(doc)}`}>
                                <Clock size={9} /> {doc.retentionYears}a
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          <button onClick={() => downloadDoc(doc)} title="Télécharger"
                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg">
                            <Download size={14} />
                          </button>
                          {!doc.archivedAt && (
                            <button onClick={() => togglePublish(doc)}
                              title={doc.isPublished ? 'Dépublier' : 'Publier sur espace employé'}
                              className={`p-1.5 rounded-lg ${doc.isPublished ? 'text-green-600 hover:bg-red-50 hover:text-red-500' : 'text-gray-400 hover:text-green-600 hover:bg-green-50'}`}>
                              <Send size={14} />
                            </button>
                          )}
                          <button onClick={() => showLog(doc)} title="Journal d'accès"
                            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg">
                            <History size={14} />
                          </button>
                          {!doc.archivedAt && (
                            <button onClick={() => archiveDoc(doc)} title="Archiver"
                              className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg">
                              <Archive size={14} />
                            </button>
                          )}
                          <button onClick={() => deleteDoc(doc.id)} title="Supprimer définitivement"
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Upload modal ── */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h2 className="font-bold text-gray-900">Ajouter un document</h2>
              <button onClick={() => { setModal(false); setFile(null) }} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Employé *</label>
                <select value={form.employeeId} onChange={e => setForm(f => ({ ...f, employeeId: e.target.value }))} className={inputCls}>
                  <option value="">Sélectionner…</option>
                  {employees.map(e => <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Type *</label>
                <select value={form.documentType} onChange={e => setForm(f => ({ ...f, documentType: e.target.value }))} className={inputCls}>
                  {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.emoji} {t.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nom *</label>
                  <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="ex: Contrat CDI 2025" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Période</label>
                  <input type="month" value={form.period} onChange={e => setForm(f => ({ ...f, period: e.target.value }))} className={inputCls} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Conservation (ans)</label>
                  <select value={form.retentionYears} onChange={e => setForm(f => ({ ...f, retentionYears: parseInt(e.target.value) }))} className={inputCls}>
                    {[1, 2, 3, 5, 10, 30, 50].map(y => <option key={y} value={y}>{y} ans</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-2 pt-5">
                  <input type="checkbox" id="confidential" checked={form.isConfidential} onChange={e => setForm(f => ({ ...f, isConfidential: e.target.checked }))} className="w-4 h-4 rounded" />
                  <label htmlFor="confidential" className="text-xs text-gray-700 cursor-pointer flex items-center gap-1">
                    <Shield size={12} className="text-orange-500" /> Confidentiel
                  </label>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Fichier * (PDF, image, Word — max 10 Mo)</label>
                <div onClick={() => fileRef.current?.click()}
                  className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center cursor-pointer hover:border-blue-300 hover:bg-blue-50 transition-colors">
                  {file ? (
                    <div>
                      <p className="text-sm font-medium text-gray-900">{file.name}</p>
                      <p className="text-xs text-gray-400">{fmtSize(file.size)}</p>
                    </div>
                  ) : (
                    <div>
                      <Upload size={20} className="mx-auto text-gray-300 mb-1" />
                      <p className="text-sm text-gray-400">Cliquer pour sélectionner</p>
                    </div>
                  )}
                </div>
                <input ref={fileRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
                  onChange={e => setFile(e.target.files?.[0] || null)} />
              </div>
              {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
              <button onClick={() => { setModal(false); setFile(null) }} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
              <button onClick={upload} disabled={uploading || !form.employeeId || !form.name || !file}
                className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg">
                {uploading ? 'Envoi…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Bulk bulletin modal ── */}
      {bulkModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setBulkModal(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="font-bold text-gray-900 flex items-center gap-2"><FileSpreadsheet size={18} /> Import bulletins de paie</h2>
              <button onClick={() => setBulkModal(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-xs text-blue-700">
                <p className="font-semibold mb-1">Comment ça marche :</p>
                <p>1. Sélectionnez les bulletins PDF de vos employés (un fichier par employé)</p>
                <p>2. Associez chaque fichier à un employé via le menu déroulant</p>
                <p>3. Choisissez la période et si les bulletins sont publiés automatiquement</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Période *</label>
                  <input type="month" value={bulkPeriod} onChange={e => setBulkPeriod(e.target.value)} className={inputCls} />
                </div>
                <div className="flex items-center gap-2 pt-5">
                  <input type="checkbox" id="autoPublish" checked={bulkAutoPublish} onChange={e => setBulkAutoPublish(e.target.checked)} className="w-4 h-4 rounded" />
                  <label htmlFor="autoPublish" className="text-xs text-gray-700 cursor-pointer flex items-center gap-1">
                    <Send size={12} className="text-green-500" /> Publier automatiquement
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Fichiers PDF</label>
                <div onClick={() => bulkFileRef.current?.click()}
                  className="border-2 border-dashed border-gray-200 rounded-lg p-4 text-center cursor-pointer hover:border-blue-300 hover:bg-blue-50 transition-colors">
                  <Upload size={20} className="mx-auto text-gray-300 mb-1" />
                  <p className="text-sm text-gray-400">Cliquer pour sélectionner plusieurs fichiers</p>
                </div>
                <input ref={bulkFileRef} type="file" multiple className="hidden" accept=".pdf"
                  onChange={e => {
                    const files = Array.from(e.target.files || [])
                    setBulkFiles(files)
                    setBulkMapping({})
                  }} />
              </div>

              {bulkFiles.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-700">{bulkFiles.length} fichier(s) — associez chacun à un employé :</p>
                  {bulkFiles.map((f, i) => (
                    <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2">
                      <span className="text-lg">📄</span>
                      <span className="text-xs text-gray-700 flex-1 truncate">{f.name}</span>
                      <span className="text-xs text-gray-400">{fmtSize(f.size)}</span>
                      <select value={bulkMapping[i] || ''} onChange={e => setBulkMapping(m => ({ ...m, [i]: e.target.value }))}
                        className="border border-gray-200 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-300 min-w-[160px]">
                        <option value="">— Employé —</option>
                        {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.firstName} {emp.lastName}</option>)}
                      </select>
                      {bulkMapping[i] ? <Check size={14} className="text-green-500 flex-shrink-0" /> : <AlertTriangle size={14} className="text-amber-400 flex-shrink-0" />}
                    </div>
                  ))}
                  <p className="text-xs text-gray-400">
                    {Object.values(bulkMapping).filter(Boolean).length} / {bulkFiles.length} associés
                  </p>
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2 sticky bottom-0 bg-white">
              <button onClick={() => setBulkModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
              <button onClick={bulkUpload}
                disabled={bulkUploading || !bulkFiles.length || !bulkPeriod || Object.values(bulkMapping).filter(Boolean).length === 0}
                className="px-4 py-2 text-sm font-medium bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg flex items-center gap-2">
                {bulkUploading ? 'Import en cours…' : <><FileSpreadsheet size={14} /> Importer {bulkFiles.length} bulletin{bulkFiles.length > 1 ? 's' : ''}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Access log modal ── */}
      {logModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setLogModal(null)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="font-bold text-gray-900 flex items-center gap-2"><History size={16} /> Journal d'accès</h2>
                <p className="text-xs text-gray-400 mt-0.5">{logModal.doc.name}</p>
              </div>
              <button onClick={() => setLogModal(null)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto flex-1 p-4">
              {logModal.logs.length === 0 ? (
                <p className="text-center text-gray-400 text-sm py-8">Aucune entrée dans le journal</p>
              ) : (
                <div className="space-y-2">
                  {logModal.logs.map(log => (
                    <div key={log.id} className="flex items-center gap-3 px-3 py-2 bg-gray-50 rounded-lg">
                      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${log.accessType === 'download' ? 'bg-blue-400' : log.accessType === 'publish' ? 'bg-green-400' : log.accessType === 'delete' ? 'bg-red-400' : 'bg-gray-400'}`} />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-800">{ACCESS_LABELS[log.accessType] || log.accessType}</span>
                        <span className="text-xs text-gray-400 ml-2">par {log.accessorType === 'employee' ? 'l\'employé' : 'le manager'}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500">{new Date(log.accessedAt).toLocaleString('fr-FR')}</div>
                        {log.ipAddress && <div className="text-xs text-gray-300">{log.ipAddress}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
